import type { Json } from '@sixam/shared/database.types';
import type {
  ComputedRoutePlan,
  RouteRoutingProfile,
  RouteWaypoint,
} from '@sixam/shared/route-planner-core';

import { authedFetch } from '@/lib/authed-fetch';
import { fetchRouteVersion, type RouteLibraryItem } from '@/lib/routes';
import { computeValhallaRoute } from '@/lib/valhalla-routing';
import { supabase } from '@sixam/supabase/client';

export async function computeRoutePlan(
  tenantId: string,
  waypoints: readonly RouteWaypoint[],
  routingProfile: RouteRoutingProfile,
  signal?: AbortSignal
): Promise<ComputedRoutePlan> {
  try {
    const response = await authedFetch('/api/routes/compute', {
      body: JSON.stringify({ routingProfile, tenantId, waypoints }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      signal,
    });
    const body = (await response.json()) as { error?: string; route?: ComputedRoutePlan };
    if (response.ok && body.route) return body.route;
    if (!__DEV__ || response.status < 500) {
      throw new Error(body.error ?? 'Could not calculate route.');
    }
  } catch (error) {
    if (!__DEV__ || (error instanceof Error && error.name === 'AbortError')) throw error;
  }

  return computeValhallaRoute(waypoints, routingProfile, signal);
}

export async function savePlannedRoute({
  description,
  name,
  plan,
  tenantId,
}: Readonly<{
  description?: string;
  name: string;
  plan: ComputedRoutePlan;
  tenantId: string;
}>): Promise<RouteLibraryItem> {
  const start = plan.waypoints[0];
  const end = plan.waypoints.at(-1)!;
  const result = await supabase.rpc('create_route_with_version', {
    p_bounds: plan.bounds as unknown as Json,
    p_description: description?.trim() ?? '',
    p_distance_m: plan.distanceM,
    p_elevation_gain_m: plan.elevationGainM ?? 0,
    p_end_lat: end.latitude,
    p_end_lng: end.longitude,
    p_geometry: plan.geometry as unknown as Json,
    p_name: name.trim(),
    p_source: 'sixam',
    p_source_metadata: {
      duration_seconds: plan.durationSeconds,
      provider: plan.provider,
      routing_profile: plan.routingProfile,
      waypoints: plan.waypoints.map((waypoint) => ({ ...waypoint })),
    },
    p_start_lat: start.latitude,
    p_start_lng: start.longitude,
    p_tenant_id: tenantId,
  });
  if (result.error) throw result.error;
  const saved = await fetchRouteVersion(result.data);
  if (!saved) throw new Error('The route saved but could not be reloaded.');
  return saved;
}
